import { useLayoutEffect, useState } from 'react';
import type { ImageSearchResultType } from '../types/type';

//원안에 들어갈 이미지 비율
const MAX_IMAGE_SIZE_RATIO = 0.8;

export const ImageScanOverlay = ({
  imageData,
  isLoading,
  selectedMember,
  size = 'default',
  isPhotoCardRatio = true
}: {
  imageData: string;
  isLoading: boolean;
  selectedMember: ImageSearchResultType['selectedMember'];
  size?: 'default' | 'sm';
  isPhotoCardRatio?: boolean;
}) => {
  const [imageSize, setImageSize] = useState<{ width: number; height: number } | null>(null);

  useLayoutEffect(() => {
    if (!imageData || isPhotoCardRatio) return;

    const img = new Image();
    img.onload = () => setImageSize({ width: img.width, height: img.height });
    img.src = imageData;
  }, [imageData, isPhotoCardRatio]);

  const isSmall = size === 'sm';
  const containerSize = isSmall ? 156 : 218.4;
  const cardBorderRadius = isSmall ? 6 : 8;
  const scanLineHeight = isSmall ? 14 : 20;

  // 기본 포토카드 사이즈
  const fixedCardWidth = isSmall ? 71 : 98;
  const fixedCardHeight = isSmall ? 113 : 157;

  // 동적 이미지 비율 계산
  let cardWidth = fixedCardWidth;
  let cardHeight = fixedCardHeight;

  const maxSize = containerSize * MAX_IMAGE_SIZE_RATIO;
  if (!isPhotoCardRatio && imageSize) {
    const aspect = imageSize.width / imageSize.height;

    if (aspect > 1) {
      // 가로가 더 길 때
      cardWidth = maxSize;
      cardHeight = maxSize / aspect;
    } else {
      // 세로가 더 길 때
      cardHeight = maxSize;
      cardWidth = maxSize * aspect;
    }

    // 원형 컨테이너를 벗어나지 않도록 추가 보정
    const radius = containerSize / 2;
    const diagonal = Math.sqrt(cardWidth * cardWidth + cardHeight * cardHeight) / 2;

    if (diagonal > radius * MAX_IMAGE_SIZE_RATIO) {
      const scale = (radius * MAX_IMAGE_SIZE_RATIO) / diagonal;
      cardWidth *= scale;
      cardHeight *= scale;
    }
  }

  // 태그 스타일 (크기별)
  const tagClasses = isSmall
    ? 'px-2 py-1 text-sm rounded shadow-sm'
    : 'px-3 py-1.5 text-base rounded-md shadow-md';

  return (
    <div className="relative" style={{ width: `${containerSize}px`, height: `${containerSize}px` }}>
      <div className="absolute inset-0 rounded-full bg-surface-light dark:bg-[#1B1C20] z-0" />

      {/* 절대 위치 컨테이너 */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
        {/* 상대 위치 컨테이너 */}
        <div
          className="relative"
          style={{
            width: `${cardWidth}px`,
            height: `${cardHeight}px`,
            borderRadius: `${cardBorderRadius}px`
          }}
        >
          <div
            className={`absolute ${isSmall ? 'bottom-[8px] -left-[47px]' : 'bottom-[8px] -left-[60px]'} rotate-[350deg] z-[10]`}
          >
            <div className={`bg-separator-light-2 dark:bg-separator-dark-2 ${tagClasses}`}>
              <p className="font-semibold text-sub-light-2 dark:text-sub-dark-2 leading-normal whitespace-nowrap">
                {selectedMember.memberName}
              </p>
            </div>
          </div>
          {/* 아티스트 태그 - 상단 오른쪽 */}
          <div
            className={`absolute ${isSmall ? 'top-[24px] -right-[47px]' : 'top-[24px] -right-[60px]'} rotate-[10deg] z-[10]`}
          >
            <div className={`bg-separator-light-2 dark:bg-separator-dark-2 ${tagClasses}`}>
              <p className="font-semibold text-sub-light-2 dark:text-sub-dark-2 leading-normal whitespace-nowrap">
                {selectedMember.artistName}
              </p>
            </div>
          </div>
          {/* 이미지 */}
          <img
            src={imageData}
            alt="Scanned"
            className="w-full h-full z-[20] relative "
            style={{
              borderRadius: `${cardBorderRadius}px`,
              objectFit: isPhotoCardRatio ? 'fill' : 'contain'
            }}
          />

          {/* 스캔라인 */}
          {isLoading && (
            <>
              {/* 동적 애니메이션 생성 */}
              <style>
                {`
                @keyframes scanLine-${Math.round(cardHeight)}-${Math.round(scanLineHeight)} {
                  0% { transform: translateY(0px); }
                  50% { transform: translateY(${cardHeight - scanLineHeight}px); }
                  100% { transform: translateY(0px); }
                }
              `}
              </style>

              <div
                className="absolute inset-0 z-[30] overflow-hidden"
                style={{ borderRadius: `${cardBorderRadius}px` }}
              >
                <div
                  className="bg-gradient-to-b from-[#5c3dfa00] to-[#5c3dfa00] via-50% via-[#3dfaeab3]"
                  style={{
                    width: `${cardWidth}px`,
                    height: `${scanLineHeight}px`,
                    animation: `scanLine-${Math.round(cardHeight)}-${Math.round(scanLineHeight)} 4s ease-in-out infinite`
                  }}
                />
              </div>
            </>
          )}
          {/* 멤버 태그 - 하단 왼쪽 */}
        </div>
      </div>
    </div>
  );
};